// src/components/Reviews/Reviews.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  CircularProgress,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Rating,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Alert,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ThumbUp,
  ThumbDown,
  Add as AddIcon,
  RateReview,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  VerifiedUser as VerifiedIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import API from '../config';
import SubscriptionModal from '../Subscription/SubscriptionModal';
import ReviewModal from '../Review/ReviewModal';
import CredentialsModal from '../Auth/CredentialsModal';
import MFAModal from '../Auth/MFAModal';
import ReportButton from './ReportButton';

export default function Reviews() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  

  // State
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userVerified, setUserVerified] = useState(true); // Assume verified until proven otherwise
  const [isVIP, setIsVIP] = useState(false);
  const [isOFVerified, setIsOFVerified] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('unverified');
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Leave review state
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [creatorSearchResults, setCreatorSearchResults] = useState([]);
  const [searchingCreators, setSearchingCreators] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCreatorUrlDialog, setShowCreatorUrlDialog] = useState(false);
  const [creatorUrl, setCreatorUrl] = useState('');
  const [creatingCreator, setCreatingCreator] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [showVerificationExplainer, setShowVerificationExplainer] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [triggeringMFA, setTriggeringMFA] = useState(false);
  
  // Creator profile modal state
  const [showCreatorProfileModal, setShowCreatorProfileModal] = useState(false);
  const [profileModalCreator, setProfileModalCreator] = useState(null);
  const [profileModalLoading, setProfileModalLoading] = useState(false);
  const [showFullReview, setShowFullReview] = useState({ open: false, creator: '', text: '' });

  // Delete review state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deletingReview, setDeletingReview] = useState(false);

  // Pending-review modal
  const [pendingOpen, setPendingOpen] = useState(false);
  const [onlyfansUrl, setOnlyfansUrl] = useState('');
  
  // Subscription modal
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewCreator, setReviewCreator] = useState(null);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [refreshingSubscriptions, setRefreshingSubscriptions] = useState(false);
  const [showRefreshCredentialsModal, setShowRefreshCredentialsModal] = useState(false);
  const [refreshCredentials, setRefreshCredentials] = useState({
    email: '',
    password: ''
  });
  const [showRefreshPassword, setShowRefreshPassword] = useState(false);
  const [refreshingWithCredentials, setRefreshingWithCredentials] = useState(false);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    recommend: '',
    spent: '',
    contentQuality: 0,
    contentFrequency: 0,
    authenticity: 0,
    consistency: 0,
    engagement: 0,
    fullReview: '',
  });

  // Check user verification status
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Fetch user profile to check verification status and subscriptions
    axios
      .get(`${API}/api/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setUserVerified(data.profile.verified);
        setVerificationStatus(data.profile.verificationStatus || 'unverified');
        setUserSubscriptions(data.profile.subscriptions || []);
      })
      .catch((err) => {
        console.error('Failed to fetch user profile:', err);
      });
  }, [token, navigate]);

  // Delete review handler
  const handleDeleteReview = (review) => {
    setReviewToDelete(review);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete || !token) return;
    
    setDeletingReview(true);
    try {
      await axios.delete(
        `${API}/api/reviews/${reviewToDelete._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review deleted successfully');
      setDeleteConfirmOpen(false);
      setReviewToDelete(null);
      
      // Refresh reviews
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error(error.response?.data?.error || 'Failed to delete review');
    } finally {
      setDeletingReview(false);
    }
  };

  // Function to fetch reviews (can be called anytime)
  const fetchReviews = useCallback(() => {
    if (!token) return;
    
    setLoading(true);
    const endpoint = `${API}/api/reviews/user`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    axios
      .get(endpoint, config)
      .then((resp) => {
        setReviews(resp.data.reviews || []);
        setIsVIP(resp.data.isVIP || false);
        setIsOFVerified(resp.data.isOFVerified || false);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load reviews.');
      })
      .finally(() => setLoading(false));
  }, [token]);
  
  // Fetch reviews on mount
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchReviews();
  }, [token, navigate, fetchReviews]);

  // Filter reviews
  const filtered = reviews.filter((r) => {
    const term = searchTerm.toLowerCase();
    const text =
      (r.creator_info?.username || '').toLowerCase() + ' ' + (r.fullReview || '').toLowerCase();
    return text.includes(term);
  });

  // Group reviews by recommendation
  const positiveReviews = filtered.filter((r) => r.recommend);
  const negativeReviews = filtered.filter((r) => !r.recommend);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get reviews based on current tab
  const getDisplayedReviews = () => {
    switch (tabValue) {
      case 0:
        return filtered;
      case 1:
        return positiveReviews;
      case 2:
        return negativeReviews;
      default:
        return filtered;
    }
  };

  // Modal handlers
  const openPending = () => {
    setOnlyfansUrl('');
    setPendingOpen(true);
  };
  
  const closePending = () => setPendingOpen(false);

  // Review handlers (same as Dashboard)
  const openReview = creator => {
    setReviewCreator(creator);
    setReviewForm({
      recommend: '',
      spent: '',
      contentQuality: 0,
      contentFrequency: 0,
      authenticity: 0,
      consistency: 0,
      engagement: 0,
      fullReview: '',
    });
    setShowReview(true);
  };

  const handleReviewSubmit = async e => {
    e.preventDefault();
    if (!token || !reviewCreator) return;
    if (hasReviewedCreator(reviewCreator)) {
      toast.info('Looks like you already reviewed this creator. You can update your original review instead.');
      return;
    }
    
    const payload = { creator_id: reviewCreator._id, ...reviewForm };
    

    
    try {
      await axios.post(`${API}/api/reviews`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Review submitted successfully!');
      setShowReview(false);
      setReviewCreator(null);
      // Refresh reviews and subscriptions to update the UI
      fetchReviews();
      // Also refresh subscriptions to update reviewed status
      axios.get(`${API}/api/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => {
          setUserSubscriptions(data.profile.subscriptions || []);
        })
        .catch(console.error);
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit review. Please try again.');
    }
  };

  // Refresh subscriptions handlers (same as Dashboard)
  const handleRefreshSubscriptions = () => {
    if (!token) return;
    // Open the credentials modal instead of directly refreshing
    setShowRefreshCredentialsModal(true);
  };

  const handleRefreshCredentialsChange = e => {
    const { name, value } = e.target;
    setRefreshCredentials(prev => ({ ...prev, [name]: value }));
  };

  const toggleRefreshPasswordVisibility = () => {
    setShowRefreshPassword(prev => !prev);
  };

  const handleRefreshWithCredentials = async e => {
    e.preventDefault();
    if (!token) return;
    
    // Validate inputs
    if (!refreshCredentials.email || !refreshCredentials.password) {
      toast.error('Please enter both email and password.');
      return;
    }



    setRefreshingWithCredentials(true);
    try {
      await axios.post(`${API}/api/subscriptions/refresh-with-credentials`, refreshCredentials, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Subscriptions refreshed successfully!');
      setShowRefreshCredentialsModal(false);
      setRefreshCredentials({ email: '', password: '' });
      // Optionally refetch profile data here
    } catch (err) {
      console.error(err);
      toast.error('Failed to refresh subscriptions. Please check your credentials.');
    } finally {
      setRefreshingWithCredentials(false);
    }
  };

  const submitPending = async (e) => {
    e.preventDefault();
    if (!onlyfansUrl) {
      toast.error('Please enter a valid OnlyFans URL.');
      return;
    }
    
    try {
      const parts = onlyfansUrl.split('/').filter(Boolean);
      const name = parts.pop();
      const endpoint = `${API}/api/reviews/add_pending_review`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(endpoint, { url: name }, config);
      toast.success('Your request has been submitted.');
      closePending();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit your request.');
    }
  };
  
  // Leave review handlers
  const handleCreatorSearch = async () => {
    if (!creatorSearchQuery.trim()) {
      setCreatorSearchResults([]);
      return;
    }
    
    setSearchingCreators(true);
    try {
      const { data } = await axios.get(`${API}/api/creators/search?q=${encodeURIComponent(creatorSearchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreatorSearchResults(data.creators || []);
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search creators');
    } finally {
      setSearchingCreators(false);
    }
  };
  
  const handleSelectCreator = (creator) => {
    setSelectedCreator(creator);
  };
  
  const hasReviewedCreator = useCallback(
    (creator) => {
      if (!creator) return false;
      const candidateIds = [creator.id, creator._id, creator.creator_id, creator.slug, creator?.creatorId].filter(Boolean);
      if (!candidateIds.length) return false;
      return reviews.some((review) => {
        const reviewIds = [
          review.creator_id,
          review.creatorId,
          review.creator_info?._id,
          review.creator_info?.id,
          review.creator_info?.creator_id,
        ].filter(Boolean);
        return reviewIds.some((id) => candidateIds.includes(id));
      });
    },
    [reviews]
  );
  const alreadyReviewedSelected = hasReviewedCreator(selectedCreator);
  
  const handleOpenReviewModal = () => {
    if (hasReviewedCreator(selectedCreator)) {
      toast.info('You already left a review for this creator. You can edit it from the My Past Reviews section.');
      return;
    }
    setShowReviewModal(true);
    // Set default values to 0 (unrated) - user must rate all categories
    setReviewForm({
      recommend: '',
      spent: '',
      contentQuality: 0,
      contentFrequency: 0,
      authenticity: 0,
      consistency: 0,
      engagement: 0,
      fullReview: '',
    });
  };
  
  // Verification handlers - matching Dashboard pattern
  const handleStartVerification = () => {
    setShowCredentialsModal(true);
  };
  
  const handleCredentialsSubmit = async ({ email, password }) => {
    setTriggeringMFA(true);
    
    try {
      setShowCredentialsModal(false);  // Close modal
      
      await axios.post(
        `${API}/api/subscriptions/trigger-mfa-verification`,
        { email, password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Verification started! Check your OnlyFans for a code.');
      
      // Start polling for status
      const startTime = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          if (Date.now() - startTime > 360000) { // 6 minutes timeout
            clearInterval(pollInterval);
            toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
            return;
          }

          const { data } = await axios.get(
            `${API}/api/subscriptions/verification-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (data.verificationStatus === 'failed') {
            clearInterval(pollInterval);
            toast.error('Verification failed. Please try again.');
            return;
          }
          
          if (data.mfaCodeSent) {
            setShowMFAModal(true);
            clearInterval(pollInterval);
          }
          
          if (data.verified) {
            clearInterval(pollInterval);
            toast.success('✅ Verification complete!');
            // Refresh to update verification status
            window.location.reload();
          }
        } catch (err) {
          clearInterval(pollInterval);
          toast.error('Error checking verification status');
        }
      }, 2000);  // Check every 2 seconds
      
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start verification');
    } finally {
      setTriggeringMFA(false);
    }
  };
  
  const handleMFASuccess = () => {
    toast.success('MFA code submitted! Your subscriptions will be fetched shortly.');
    setShowMFAModal(false);
    
    // Start polling for completion
    const startTime = Date.now();
    const pollInterval = setInterval(async () => {
      try {
        if (Date.now() - startTime > 360000) { // 6 minutes
          clearInterval(pollInterval);
          toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
          return;
        }

        const { data } = await axios.get(
          `${API}/api/subscriptions/verification-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (data.verificationStatus === 'failed') {
          clearInterval(pollInterval);
          toast.error('Verification failed. Please try again.');
          return;
        }
        
        if (data.verified && data.hasSubscriptions) {
          clearInterval(pollInterval);
          toast.success('✅ Verification complete! Subscriptions loaded.');
          // Refresh page to update everything
          window.location.reload();
        }
      } catch (err) {
        clearInterval(pollInterval);
        toast.error('Error checking verification status');
      }
    }, 3000);
  };
  
  const handleViewCreatorProfile = async (creator) => {
    setProfileModalLoading(true);
    setShowCreatorProfileModal(true);
    try {
      const { data } = await axios.get(`${API}/api/creators/${creator.id || creator._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfileModalCreator(data.creator);
    } catch (err) {
      console.error('Failed to load creator:', err);
      toast.error('Failed to load creator profile');
      setShowCreatorProfileModal(false);
    } finally {
      setProfileModalLoading(false);
    }
  };
  
  const handleSubmitNewReview = async (e) => {
    e.preventDefault();
    
    if (!selectedCreator) {
      toast.error('Please select a creator first');
      return;
    }
    
    // Validate all ratings are filled
    if (reviewForm.contentQuality === 0 || reviewForm.contentFrequency === 0 || 
        reviewForm.authenticity === 0 || reviewForm.consistency === 0 || reviewForm.engagement === 0) {
      toast.error('Please rate all categories before submitting');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/api/reviews`,
        {
          creator_id: selectedCreator.id,
          creator_onlyfans_id: selectedCreator.user_id,
          ...reviewForm,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review submitted successfully!');
      setShowReviewModal(false);
      
      // Refresh reviews list
      const endpoint = `${API}/api/reviews/user`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      axios.get(endpoint, config).then((resp) => {
        setReviews(resp.data.reviews || []);
      });
      
      // Show VIP modal if not VIP
      if (!isVIP) {
        setShowVIPModal(true);
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      toast.error(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };
  
  const validateOnlyFansUrl = (url) => {
    if (!url) return false;
    const cleanUrl = url.toLowerCase().trim();
    
    // Check if URL contains onlyfans.com
    if (!cleanUrl.includes('onlyfans.com')) {
      return false;
    }
    
    // Check if it's not just the homepage
    if (cleanUrl.endsWith('onlyfans.com') || cleanUrl.endsWith('onlyfans.com/')) {
      return false;
    }
    
    return true;
  };

  const handleCreateFromUrl = async () => {
    if (!creatorUrl.trim()) {
      toast.error('Please enter a valid OnlyFans URL');
      return;
    }
    
    // Validate URL format
    if (!validateOnlyFansUrl(creatorUrl)) {
      toast.error('Please enter a valid OnlyFans profile URL (e.g., https://onlyfans.com/username)');
      return;
    }
    
    setCreatingCreator(true);
    try {
      const { data } = await axios.post(
        `${API}/api/creators/scrape-profile`,
        { of_url: creatorUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedCreator(data.creator);
      setShowCreatorUrlDialog(false);
      setCreatorUrl('');
      
      if (data.fromDatabase) {
        toast.success('✨ Creator found in our database! You can leave a review now.');
      } else {
        toast.success('✅ Creator profile retrieved successfully! You can leave a review now.');
      }
    } catch (err) {
      console.error('Error creating creator:', err);
      toast.error(err.response?.data?.error || 'Failed to create creator profile');
    } finally {
      setCreatingCreator(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
          Loading your reviews...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: '1400px', mx: 'auto', py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
      {/* Verification Required Alert - Only show during active verification */}
      {!userVerified && ['processing', 'queued', 'mfa_triggered', 'mfa_verification_triggered', 'mfa_code_submitted'].includes(verificationStatus) && (
        <Alert 
          severity="info" 
          sx={{ mb: 4, borderRadius: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Verification in Progress
          </Typography>
          <Typography variant="body2">
            We're verifying your OnlyFans account and loading your subscriptions.
            You'll be able to review creators shortly (usually 2-5 minutes).
            <br /><br />
            The page will update automatically when verification is complete.
            You can explore the site while you wait!
          </Typography>
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Reviews
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Leave reviews and see what you've said about creators
        </Typography>
      </Box>
      
      {/* Verification Banner for Unverified Users */}
      {!isOFVerified && (
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
              theme.palette.info.main,
              0.08
            )})`,
            border: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <VerifiedIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Unlock Verified Reviews
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verify your OnlyFans account to leave <strong>verified reviews</strong> that carry more weight and help others make better decisions.
                Verified reviews show a green checkmark and are trusted by the community!
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartVerification}
              sx={{
                py: 1.5,
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                flexShrink: 0,
                minWidth: 140,
              }}
            >
              Verify Now
            </Button>
          </Stack>
        </Paper>
      )}
      
      {/* Your OF Subscriptions Section - Only show if user has subscriptions */}
      {userSubscriptions && userSubscriptions.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.info.main, 0.05)})` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VerifiedIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Your OF Subscriptions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quick access to review your OnlyFans subscriptions
              </Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              maxHeight: {
                xs: showAllSubscriptions ? 'none' : '400px',
                md: 'none',
              },
              overflow: {
                xs: showAllSubscriptions ? 'visible' : 'hidden',
                md: 'visible',
              },
              position: 'relative',
            }}
          >
            {userSubscriptions.map((sub) => {
              const creator = sub.creator;
              const isReviewed = sub.reviewed;
              
              return (
                <Card
                  key={creator?.user_id || creator?._id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderRadius: 2,
                    position: 'relative',
                    border: `2px solid ${isReviewed ? theme.palette.success.main : theme.palette.divider}`,
                    opacity: isReviewed ? 0.7 : 1,
                    '&:hover': {
                      transform: isReviewed ? 'none' : 'translateY(-2px)',
                      boxShadow: isReviewed ? 1 : 3,
                      borderColor: isReviewed ? theme.palette.success.main : theme.palette.primary.light,
                    },
                  }}
                  onClick={() => {
                    if (!isReviewed) {
                      openReview(creator);
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', px: 2, py: 3, position: 'relative' }}>
                    {isReviewed && (
                      <Chip
                        label="Reviewed"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                    <Avatar
                      src={creator?.profilePicture}
                      alt={creator?.username}
                      sx={{
                        width: 72,
                        height: 72,
                        mx: 'auto',
                        mb: 2,
                        border: `3px solid ${isReviewed ? theme.palette.success.main : theme.palette.primary.main}`,
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={700} noWrap gutterBottom>
                      {creator?.username || 'Unknown'}
                    </Typography>
                    {!isReviewed && (
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Leave Review
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
          
          {/* Show More/Less Button - Only on mobile when there are many subscriptions */}
          {isMobile && userSubscriptions.length > 6 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setShowAllSubscriptions(!showAllSubscriptions)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                }}
              >
                {showAllSubscriptions ? 'Show Less' : `Show All ${userSubscriptions.length} Subscriptions`}
              </Button>
            </Box>
          )}
        </Paper>
      )}
      
      {/* Leave a Review Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})` }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          Leave a Review
        </Typography>
        
        {/* Search Section - Full Width */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            Search for Creator
          </Typography>
          
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              fullWidth
              placeholder="Search by creator username..."
              value={creatorSearchQuery}
              onChange={(e) => setCreatorSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreatorSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.common.white, 0.7),
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleCreatorSearch}
              disabled={searchingCreators || !creatorSearchQuery.trim()}
              sx={{
                minWidth: { xs: '100%', sm: 140 },
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {searchingCreators ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </Stack>

          {/* Search Results */}
          {creatorSearchResults.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(180px, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                overflowX: { xs: 'auto', md: 'visible' },
                pb: { xs: 1, md: 0 },
              }}
            >
              {creatorSearchResults.map((creator) => {
                const isSelected =
                  selectedCreator &&
                  ((creator.id && selectedCreator.id === creator.id) ||
                   (creator._id && selectedCreator._id === creator._id) ||
                   (creator.creator_id && selectedCreator.creator_id === creator.creator_id));
                return (
                  <Card
                    key={creator.id || creator._id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      borderRadius: 2,
                      position: 'relative',
                      border: `2px solid ${
                        isSelected ? theme.palette.primary.main : theme.palette.divider
                      }`,
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                        borderColor: theme.palette.primary.light,
                      },
                    }}
                    onClick={() => handleSelectCreator(creator)}
                  >
                    <CardContent
                      sx={{
                        textAlign: 'center',
                        position: 'relative',
                        px: 3,
                        py: 3,
                      }}
                    >
                      {isSelected && (
                        <CheckCircleIcon
                          color="primary"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            fontSize: 28,
                          }}
                        />
                      )}
                      <Avatar
                        src={creator.profilePicture}
                        alt={creator.username}
                        sx={{
                          width: 72,
                          height: 72,
                          mx: 'auto',
                          mb: 2,
                          border: `3px solid ${
                            isSelected ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.1)
                          }`,
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom noWrap>
                        {creator.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isSelected ? 'Selected' : 'Tap to select'}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}

          {/* Creator Not Found Button */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Can't find the creator you're looking for?
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setShowCreatorUrlDialog(true)}
              sx={{ mt: 1 }}
            >
              Add Creator by URL
            </Button>
          </Box>
        </Box>

        {/* Selected Creator Profile - Full Width Below Search */}
        {selectedCreator && (
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              position: 'relative',
              backgroundColor: theme.palette.background.paper,
              borderTop: `4px solid ${theme.palette.primary.main}`,
              mb: 3,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar
                  src={selectedCreator.profilePicture}
                  alt={selectedCreator.username}
                  sx={{
                    width: { xs: 80, sm: 96 },
                    height: { xs: 80, sm: 96 },
                    border: `3px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom noWrap color="text.primary">
                    {selectedCreator.username}
                  </Typography>
                  <Chip
                    label="OnlyFans Creator"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: 0.3,
                    }}
                  />
                  {alreadyReviewedSelected && (
                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        color="success"
                        variant="outlined"
                        size="small"
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Review submitted"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>

              <Stack
                spacing={2}
                direction={{ xs: 'column', sm: 'row' }}
                sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', md: 'auto' },
                  ml: { md: 'auto' },
                }}
              >
                <Tooltip
                  title={
                    alreadyReviewedSelected
                      ? 'You already reviewed this creator. Update it from My Past Reviews.'
                      : ''
                  }
                  placement="top"
                  disableHoverListener={!alreadyReviewedSelected}
                >
                  <span style={{ width: '100%' }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<RateReview />}
                      onClick={handleOpenReviewModal}
                      disabled={alreadyReviewedSelected}
                      sx={{
                        py: 1.5,
                        px: 3,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        minWidth: { sm: 180 },
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4,
                          transform: alreadyReviewedSelected ? 'none' : 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {alreadyReviewedSelected ? 'Already Reviewed' : 'Leave Review'}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => handleViewCreatorProfile(selectedCreator)}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    minWidth: { sm: 180 },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  View Profile →
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Paper>
      
      {/* My Past Reviews Section */}
      <Box sx={{ mb: 3, mt: 6 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          My Past Reviews
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and search through all the reviews you've submitted
        </Typography>
      </Box>

      {/* Search and filters */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(
            theme.palette.background.paper,
            0.98
          )})`,
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Search by creator name or review content..."
              variant="outlined"
              size="medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '1rem',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="medium" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="medium" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs - Modern Pill Style */}
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        justifyContent: 'center',
      }}>
        <Box sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 4,
          p: 0.75,
          display: 'flex',
          gap: { xs: 0.5, sm: 1 },
          boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.08)}`,
        }}>
          <Button
            onClick={() => handleTabChange(null, 0)}
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              minWidth: { xs: 'auto', sm: 140 },
              transition: 'all 0.3s ease',
              ...(tabValue === 0 && {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }),
              ...( tabValue !== 0 && {
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.05),
                  color: theme.palette.text.primary,
                },
              }),
            }}
          >
            All Reviews
            <Chip 
              label={filtered.length} 
              size="small" 
              sx={{ 
                ml: 1,
                height: 22, 
                fontSize: '0.75rem',
                fontWeight: 700,
                ...(tabValue === 0 ? {
                  backgroundColor: alpha(theme.palette.common.white, 0.25),
                  color: theme.palette.common.white,
                } : {
                  backgroundColor: alpha(theme.palette.action.selected, 0.1),
                  color: theme.palette.text.secondary,
                }),
              }} 
            />
          </Button>
          <Button
            onClick={() => handleTabChange(null, 1)}
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              minWidth: { xs: 'auto', sm: 140 },
              transition: 'all 0.3s ease',
              ...(tabValue === 1 && {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.common.white,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark,
                },
              }),
              ...(tabValue !== 1 && {
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  color: theme.palette.success.main,
                },
              }),
            }}
          >
            <ThumbUp fontSize="small" sx={{ mr: 1 }} />
            Positive
            <Chip 
              label={positiveReviews.length} 
              size="small" 
              sx={{ 
                ml: 1,
                height: 22, 
                fontSize: '0.75rem',
                fontWeight: 700,
                ...(tabValue === 1 ? {
                  backgroundColor: alpha(theme.palette.common.white, 0.25),
                  color: theme.palette.common.white,
                } : {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                }),
              }} 
            />
          </Button>
          <Button
            onClick={() => handleTabChange(null, 2)}
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              minWidth: { xs: 'auto', sm: 140 },
              transition: 'all 0.3s ease',
              ...(tabValue === 2 && {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.common.white,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }),
              ...(tabValue !== 2 && {
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                  color: theme.palette.error.main,
                },
              }),
            }}
          >
            <ThumbDown fontSize="small" sx={{ mr: 1 }} />
            Negative
            <Chip 
              label={negativeReviews.length} 
              size="small" 
              sx={{ 
                ml: 1,
                height: 22, 
                fontSize: '0.75rem',
                fontWeight: 700,
                ...(tabValue === 2 ? {
                  backgroundColor: alpha(theme.palette.common.white, 0.25),
                  color: theme.palette.common.white,
                } : {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                }),
              }} 
            />
          </Button>
        </Box>
      </Box>


      {/* Reviews grid */}
      {getDisplayedReviews().length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.7),
          }}
        >
          <Typography variant="h6" gutterBottom>
            No reviews found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchTerm
              ? 'Try adjusting your search criteria'
              : 'You haven\'t submitted any reviews yet'}
          </Typography>
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={() => setSearchTerm('')}
              sx={{ mt: 1 }}
            >
              Clear Search
            </Button>
          )}
        </Paper>
      ) : (
        <Grid
          container
          spacing={4}
          justifyContent="center"
        >
          {getDisplayedReviews().map((review, index) => {
            // Calculate overall quality as average of all ratings
            const overallQuality = (
              (review.contentQuality || 0) +
              (review.contentFrequency || 0) +
              (review.authenticity || 0) +
              (review.consistency || 0) +
              (review.engagement || 0)
            ) / 5;
            
            return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={review._id}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                animation: `fadeInUp 0.5s ease-out ${index * 0.1}s backwards`,
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(30px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Card
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: review.verified 
                    ? `2px solid ${alpha(theme.palette.success.main, 0.3)}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  width: '100%',
                  maxWidth: 380,
                  height: 600,
                  mx: 'auto',
                  background: theme.palette.background.paper,
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.15)}`,
                    borderColor: review.verified
                      ? alpha(theme.palette.success.main, 0.6)
                      : alpha(theme.palette.primary.main, 0.4),
                    '& .card-avatar': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                    '& .card-button': {
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 4 }}>
                {/* Verified/Unverified Badge and Delete Button */}
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Delete button - all reviews here are the user's own reviews */}
                  <Tooltip title="Delete review">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReview(review);
                      }}
                      sx={{
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.2),
                          borderColor: alpha(theme.palette.error.main, 0.4),
                        },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  
                  {review.verified ? (
                    <Chip
                      label="VERIFIED REVIEW"
                      size="small"
                      icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                      sx={{
                        height: 28,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        border: `2px solid ${theme.palette.background.paper}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                        '& .MuiChip-icon': {
                          color: 'white',
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      label="UNVERIFIED REVIEW"
                      size="small"
                      sx={{
                        height: 28,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: theme.palette.grey[700],
                        color: 'white',
                        border: `2px solid ${theme.palette.background.paper}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  )}
                </Box>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      src={review.creator_info?.profilePicture || '/default-profile.png'}
                      alt={review.creator_info?.username}
                      className="card-avatar"
                      sx={{ 
                        width: 64, 
                        height: 64,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1.15rem' }} noWrap>
                        {review.creator_info?.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Typography>
                      <Chip
                        icon={review.recommend ? <ThumbUp /> : <ThumbDown />}
                        label={review.recommend ? 'Recommended' : 'Not Recommended'}
                        size="small"
                        sx={{ 
                          fontWeight: 700,
                          height: 26,
                          fontSize: '0.75rem',
                          background: review.recommend 
                            ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                            : `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                          color: 'white',
                          boxShadow: `0 2px 8px ${alpha(review.recommend ? theme.palette.success.main : theme.palette.error.main, 0.3)}`,
                          border: 'none',
                          '& .MuiChip-icon': { color: 'white', fontSize: '1rem' },
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
                
                {/* Overall Rating - Prominent */}
                <Box sx={{ 
                  mx: 3,
                  mb: 2,
                  p: 2.5,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.light, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', mb: 1, display: 'block' }}>
                    Overall Quality
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h3" fontWeight={800} color="warning.main" sx={{ lineHeight: 1 }}>
                      {overallQuality.toFixed(1)}
                    </Typography>
                    <Typography variant="h5" color="text.secondary" fontWeight={600}>
                      /5
                    </Typography>
                  </Box>
                  <Rating
                    value={overallQuality}
                    precision={0.1}
                    size="small"
                    readOnly
                    sx={{ 
                      mt: 1,
                      '& .MuiRating-iconFilled': { color: theme.palette.warning.main },
                      '& .MuiRating-icon': { fontSize: '1.1rem' },
                    }}
                  />
                </Box>
                
                {/* Content */}
                <Box sx={{ px: 3, pb: 2, flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  {/* Review text - always shown */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.grey[50], 0.3),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      height: 60,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: review.fullReview ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': review.fullReview ? {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      } : {},
                    }}
                    onClick={() => {
                      if (review.fullReview) {
                        setShowFullReview({
                          open: true,
                          creator: review.creator_info?.username,
                          text: review.fullReview,
                        });
                      }
                    }}
                  >
                    {review.fullReview ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                        }}
                      >
                        "{review.fullReview}"
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.disabled',
                          lineHeight: 1.6,
                          fontStyle: 'italic',
                          textAlign: 'center',
                        }}
                      >
                        No text review added
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Metrics Grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2, flexShrink: 0 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Frequency
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ fontSize: '1rem' }}>
                        {review.contentFrequency}/5
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Authenticity
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ fontSize: '1rem' }}>
                        {review.authenticity}/5
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Engagement
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ fontSize: '1rem' }}>
                        {review.engagement}/5
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Amount Spent */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    flexShrink: 0,
                  }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Investment
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="success.main">
                      {review.spent ? `$${review.spent}` : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Footer */}
                <Box sx={{ px: 3, pb: 3, pt: 1, flexShrink: 0 }}>
                  <Button
                    component={RouterLink}
                    to={`/creator/${review.creator_id}`}
                    fullWidth
                    variant="contained"
                    size="large"
                    className="card-button"
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      py: 1.5,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      }
                    }}
                  >
                    View Creator Profile
                  </Button>
                </Box>
                </Box>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Request creator dialog */}
      <Dialog 
        open={pendingOpen} 
        onClose={closePending}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Request New Creator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Enter the OnlyFans URL of the creator you'd like to review
          </Typography>
          <Box component="form" onSubmit={submitPending}>
            <TextField
              label="OnlyFans URL"
              type="url"
              value={onlyfansUrl}
              onChange={(e) => setOnlyfansUrl(e.target.value)}
              placeholder="https://onlyfans.com/creatorname"
              fullWidth
              required
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closePending} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={submitPending} 
            variant="contained"
            sx={{ px: 3 }}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        subscriptions={null} // Will use mock data in dev mode
        onOpenReview={openReview} // Use working review function
        onRefreshSubscriptions={handleRefreshSubscriptions} // Use working refresh function
        refreshingSubscriptions={refreshingSubscriptions}
      />

      {/* Review Modal */}
      <ReviewModal
        open={showReview}
        onClose={() => setShowReview(false)}
        reviewCreator={reviewCreator}
        reviewForm={reviewForm}
        setReviewForm={setReviewForm}
        onSubmit={handleReviewSubmit}
      />

      {/* Refresh Credentials Modal (same as Dashboard) */}
      <Dialog
        open={showRefreshCredentialsModal}
        onClose={() => setShowRefreshCredentialsModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Refresh Subscriptions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your OnlyFans credentials to refresh your subscription list
          </Typography>
          <Box component="form" onSubmit={handleRefreshWithCredentials}>
            <TextField
              label="Email"
              type="email"
              name="email"
              value={refreshCredentials.email}
              onChange={handleRefreshCredentialsChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type={showRefreshPassword ? 'text' : 'password'}
              name="password"
              value={refreshCredentials.password}
              onChange={handleRefreshCredentialsChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleRefreshPasswordVisibility}
                      edge="end"
                    >
                      {showRefreshPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRefreshCredentialsModal(false)}>Cancel</Button>
          <Button 
            onClick={handleRefreshWithCredentials} 
            variant="contained"
            disabled={refreshingWithCredentials}
          >
            {refreshingWithCredentials ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* New Review Modal - Using Updated ReviewModal Component */}
      <ReviewModal
        open={showReviewModal}
        onClose={() => !submittingReview && setShowReviewModal(false)}
        reviewCreator={{
          ...selectedCreator,
          creatorName: selectedCreator?.username,
          avatar: selectedCreator?.profilePicture,
        }}
        reviewForm={reviewForm}
        setReviewForm={setReviewForm}
        onSubmit={handleSubmitNewReview}
      />

      {/* Creator URL Dialog */}
      <Dialog
        open={showCreatorUrlDialog}
        onClose={() => !creatingCreator && setShowCreatorUrlDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        {creatingCreator ? (
          <LoadingCreatorScreen />
        ) : (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <AddIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Add Creator by URL
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1, mb: 3 }}>
                Enter the OnlyFans profile URL of the creator you'd like to review.
                We'll scrape their public profile information automatically.
              </Typography>
              <TextField
                fullWidth
                label="OnlyFans URL"
                placeholder="https://onlyfans.com/username"
                value={creatorUrl}
                onChange={(e) => setCreatorUrl(e.target.value)}
                disabled={creatingCreator}
                helperText="Example: https://onlyfans.com/creatorname"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={() => {
                  setShowCreatorUrlDialog(false);
                  setCreatorUrl('');
                }}
                disabled={creatingCreator}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateFromUrl}
                variant="contained"
                disabled={creatingCreator || !creatorUrl.trim()}
                startIcon={!creatingCreator && <AddIcon />}
                sx={{ minWidth: 140 }}
              >
                {creatingCreator ? <CircularProgress size={20} /> : 'Add Creator'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* VIP Marketing Modal */}
      <Dialog
        open={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
              theme.palette.secondary.main,
              0.08
            )})`,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Review Submitted! 🎉
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mt: 2, mb: 3 }}>
            Want to see how other people rated the same creator?
          </Typography>
          
          <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
            Join VIP and see what others are saying today
          </Typography>
          
          <Stack spacing={2} sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/subscription')}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              Upgrade to VIP
            </Button>
            <Button variant="outlined" onClick={() => setShowVIPModal(false)}>
              Maybe Later
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      
      {/* Credentials Modal */}
      <CredentialsModal
        open={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        onSubmit={handleCredentialsSubmit}
        loading={triggeringMFA}
      />
      
      {/* MFA Modal */}
      <MFAModal
        open={showMFAModal}
        onClose={() => setShowMFAModal(false)}
        onSuccess={handleMFASuccess}
        token={token}
      />
      
      {/* Creator Profile Modal */}
      <Dialog
        open={showCreatorProfileModal}
        onClose={() => setShowCreatorProfileModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        {profileModalLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : profileModalCreator ? (
          <>
            <DialogTitle sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>
                  Creator Profile
                </Typography>
                <IconButton onClick={() => setShowCreatorProfileModal(false)}>
                  <ClearIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
              {/* Banner & Avatar */}
              <Box sx={{ position: 'relative', mb: 6 }}>
                <Box
                  sx={{
                    height: { xs: 150, sm: 200 },
                    backgroundImage: `url(${profileModalCreator.bannerImage || '/default-banner.jpg'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <Avatar
                  src={profileModalCreator.profilePicture || '/default-profile.png'}
                  alt={profileModalCreator.username}
                  sx={{
                    width: 96,
                    height: 96,
                    border: `4px solid ${theme.palette.background.paper}`,
                    position: 'absolute',
                    bottom: -48,
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                />
              </Box>

              {/* Profile Details */}
              <Box sx={{ textAlign: 'center', px: 3, mb: 3 }}>
                <Typography variant="h4" component="h2" gutterBottom fontWeight={700}>
                  {profileModalCreator.username}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  href={profileModalCreator.profile_url}
                  target="_blank"
                  rel="noopener"
                  sx={{ mb: 2 }}
                >
                  View OnlyFans Profile →
                </Button>
                {profileModalCreator.bio && (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    {profileModalCreator.bio}
                  </Typography>
                )}

                {/* Stats */}
                {profileModalCreator.reviews?.length > 0 && (
                  <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                    <Grid item>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Avg Quality
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {(profileModalCreator.reviews.reduce((sum, r) => sum + r.contentQuality, 0) / profileModalCreator.reviews.length).toFixed(1)} / 5
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Recommend
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {Math.round((profileModalCreator.reviews.filter(r => r.recommend).length / profileModalCreator.reviews.length) * 100)}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Reviews
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {profileModalCreator.reviews.length}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Reviews Section */}
              <Box sx={{ px: 3, pb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Reviews
                </Typography>
                
                {profileModalCreator.reviews?.length === 0 ? (
                  <Box
                    sx={{
                      mt: 3,
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
                        theme.palette.secondary.main,
                        0.08
                      )})`,
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.15)}, transparent)`,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -50,
                        left: -50,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(theme.palette.info.main, 0.15)}, transparent)`,
                      },
                    }}
                  >
                    {/* Trophy Icon */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                        mb: 3,
                        position: 'relative',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.4)}`,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            transform: 'scale(1)',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.4)}`,
                          },
                          '50%': {
                            transform: 'scale(1.05)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.warning.main, 0.6)}`,
                          },
                        },
                      }}
                    >
                      <TrophyIcon sx={{ fontSize: 40, color: 'white' }} />
                      <StarIcon
                        sx={{
                          position: 'absolute',
                          top: -5,
                          right: -5,
                          fontSize: 24,
                          color: theme.palette.warning.light,
                          animation: 'sparkle 1.5s ease-in-out infinite',
                          '@keyframes sparkle': {
                            '0%, 100%': { opacity: 1, transform: 'rotate(0deg) scale(1)' },
                            '50%': { opacity: 0.5, transform: 'rotate(180deg) scale(0.8)' },
                          },
                        }}
                      />
                    </Box>

                    {/* Main Message */}
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      gutterBottom
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                        position: 'relative',
                      }}
                    >
                      Be the First Reviewer! 🎉
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                      No one has reviewed <strong>{profileModalCreator.username}</strong> yet.
                      Your experience could help others make informed decisions!
                    </Typography>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 3,
                        py: 1.5,
                        borderRadius: 3,
                        backgroundColor: theme.palette.background.paper,
                        border: `2px solid ${theme.palette.success.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
                      <Typography variant="body2" fontWeight={700} color="text.primary">
                        Your review will be featured first
                      </Typography>
                    </Box>
                  </Box>
                ) : !isVIP && profileModalCreator.reviews.length > 0 ? (
                  <Box
                    sx={{
                      mt: 3,
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(
                        theme.palette.warning.light,
                        0.05
                      )})`,
                      border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.warning.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                      }}
                    >
                      <LockIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Want to see what others have to say about this creator?
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                      Get VIP today to access all user reviews and unlock premium features!
                    </Typography>
                    
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        setShowCreatorProfileModal(false);
                        navigate('/subscription');
                      }}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: 2,
                        textTransform: 'none',
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Upgrade to VIP
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {profileModalCreator.reviews.slice(0, 6).map((review, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card
                          sx={{
                            height: '100%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 3,
                            },
                          }}
                        >
                          <CardHeader
                            avatar={
                              <Avatar
                                src={review.user?.profilePicture || '/default-profile.png'}
                                alt={review.user?.username || 'Anonymous'}
                                sx={{ width: 36, height: 36 }}
                              />
                            }
                            title={
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {review.user?.username || 'Anonymous'}
                              </Typography>
                            }
                            subheader={
                              <Typography variant="caption" color="text.secondary">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </Typography>
                            }
                            action={
                              <Chip
                                icon={review.recommend ? <ThumbUp fontSize="small" /> : <ThumbDown fontSize="small" />}
                                label={review.recommend ? '👍' : '👎'}
                                color={review.recommend ? 'success' : 'error'}
                                size="small"
                                variant="outlined"
                              />
                            }
                            sx={{ pb: 1 }}
                          />
                          <CardContent sx={{ pt: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Rating
                                value={review.contentQuality}
                                precision={0.5}
                                size="small"
                                readOnly
                              />
                              <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>
                                {review.contentQuality}/5
                              </Typography>
                            </Box>
                            {review.fullReview && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontStyle: 'italic',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                "{review.fullReview}"
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </DialogContent>
          </>
        ) : null}
      </Dialog>
      
      {/* Full Review Modal */}
      <Dialog
        open={showFullReview.open}
        onClose={() => setShowFullReview({ open: false, creator: '', text: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Review for {showFullReview.creator}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
            {showFullReview.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFullReview({ open: false, creator: '', text: '' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// Helper Components
function LoadingCreatorScreen() {
  const [messageIndex, setMessageIndex] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const theme = useTheme();
  
  const messages = [
    { text: 'Gathering creator details...', icon: '🔍' },
    { text: 'Fetching profile picture...', icon: '📸' },
    { text: 'Loading banner image...', icon: '🖼️' },
    { text: 'Reading creator bio...', icon: '📝' },
    { text: 'Almost there...', icon: '✨' },
  ];
  
  React.useEffect(() => {
    // Cycle through messages
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 2000);
    
    // Animate progress bar slowly (reach 95% in about 25 seconds)
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        // Slower increment: adds 3-5% every second
        return prev + (Math.random() * 2 + 3);
      });
    }, 1000);
    
    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, []);
  
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
      {/* Animated Icon */}
      <Box
        sx={{
          fontSize: '5rem',
          mb: 4,
          animation: 'bounce 1s ease-in-out infinite',
          '@keyframes bounce': {
            '0%, 100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-10px)' },
          },
        }}
      >
        {messages[messageIndex].icon}
      </Box>
      
      {/* Message */}
      <Typography 
        variant="h6" 
        fontWeight={600} 
        gutterBottom
        sx={{
          animation: 'fadeIn 0.5s ease-in',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(10px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {messages[messageIndex].text}
      </Typography>
      
      {/* Progress Bar */}
      <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
        <Box
          sx={{
            width: '100%',
            height: 6,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${progress}%`,
              height: '100%',
              bgcolor: theme.palette.primary.main,
              transition: 'width 0.3s ease',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {Math.min(Math.round(progress), 95)}% complete
        </Typography>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
        We're fetching the creator's public profile from OnlyFans.
        This usually takes 20-30 seconds.
      </Typography>
    </Box>
  );
}

function FormControlRadio({ value, label, color }) {
  const theme = useTheme();
  return (
    <FormControlLabel
      value={value}
      control={<Radio />}
      label={label}
      sx={{
        flex: 1,
        m: 0,
        mr: 2,
        p: 2,
        borderRadius: 2,
        border: `2px solid ${theme.palette.divider}`,
        '&:has(:checked)': {
          borderColor: color === 'success' ? theme.palette.success.main : theme.palette.error.main,
          backgroundColor: alpha(
            color === 'success' ? theme.palette.success.main : theme.palette.error.main,
            0.1
          ),
        },
      }}
    />
  );
}

function AnimatedRatingField({ label, value, onChange, icon }) {
  const theme = useTheme();
  const [hover, setHover] = React.useState(-1);
  const isUnrated = value === 0;
  const displayValue = hover !== -1 ? hover : value;
  
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: `2px solid ${isUnrated ? theme.palette.error.main : theme.palette.divider}`,
        backgroundColor: isUnrated ? alpha(theme.palette.error.main, 0.03) : alpha(theme.palette.primary.main, 0.02),
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: isUnrated ? theme.palette.error.main : theme.palette.primary.main,
          backgroundColor: isUnrated ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.primary.main, 0.08),
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '1.8rem' }}>{icon}</Typography>
          <Typography variant="body1" fontWeight={600}>
            {label}
          </Typography>
          {isUnrated && (
            <Chip 
              label="Required" 
              size="small" 
              color="error" 
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                fontWeight: 700,
              }} 
            />
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Rating
          value={value}
          onChange={(event, newValue) => {
            if (newValue) onChange(newValue);
          }}
          onChangeActive={(event, newHover) => {
            setHover(newHover);
          }}
          size="large"
          sx={{
            '& .MuiRating-iconEmpty': {
              color: isUnrated ? theme.palette.error.light : theme.palette.action.disabled,
            },
            '& .MuiRating-iconFilled': {
              color: theme.palette.warning.main,
              transform: 'scale(1.15)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
            },
            '& .MuiRating-iconHover': {
              color: theme.palette.warning.light,
              transform: 'scale(1.3)',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
            },
            '& .MuiRating-icon': {
              fontSize: '2.5rem',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              margin: '0 2px',
            },
          }}
        />
        <Chip
          label={displayValue === 0 ? 'Not rated' : `${displayValue}/5`}
          size="medium"
          sx={{
            fontWeight: 700,
            fontSize: '0.9rem',
            backgroundColor: displayValue === 0 ? theme.palette.grey[200] : alpha(theme.palette.warning.main, 0.15),
            color: displayValue === 0 ? theme.palette.text.secondary : theme.palette.warning.dark,
            border: `2px solid ${displayValue === 0 ? theme.palette.grey[300] : theme.palette.warning.main}`,
          }}
        />
      </Box>
    </Box>
  );
}
